import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import colors from 'Common/constants/colors';
import { useDropzone } from 'react-dropzone';
import { AddPhotoAlternateSharp, PhotoCameraSharp } from '@material-ui/icons';

const SimpleWrapper = styled.div`
    width: 100%;
    padding: 20px;
    background: ${colors.DARKEST_GRAY};
    border: 1px solid ${colors.DARKEST_GRAY};
    color: ${colors.LIGHTEST_GRAY};
    text-align: center;
    border-radius: 4px;

    @media screen and (min-width: 620px) {
        max-width: 400px;
    }

    @media screen and (min-width: 940px) {
        padding: 40px;
    }
`;

const ClickableWrapper = styled(SimpleWrapper).attrs({
    as: 'button',
})`
    outline: 0 none;
    cursor: pointer;

    &:hover,
    &:focus {
        background: ${colors.ALT_DARKEST_GRAY};
    }
`;

const Title = styled.div`
    margin-bottom: 5px;
    font-weight: 600;
    font-size: 18px;

    @media screen and (min-width: 375px) {
        font-size: 21px;
    }
`;

const SubTitle = styled.div`
    margin-bottom: 5px;
    font-weight: 400;
    font-size: 16px;
    font-style: italic;

    @media screen and (min-width: 375px) {
        font-size: 18px;
    }
`;

const Hint = styled.div`
    color: ${colors.ORANGE};
    font-weight: 600;
    font-size: 13px;
    line-height: 20px;
    margin-bottom: 20px;
`;

const ButtonsWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    button {
        margin-right: 10px;

        &:last-child {
            margin-right: 0;
        }
    }
`;

const SimpleIcon = styled.div`
    svg {
        color: ${colors.LIGHTEST_GRAY};
        font-size: 50px !important;
    }
`;

const IconButton = styled(SimpleIcon).attrs({
    as: 'button',
})`
    background: none;
    border: 0 none;
    outline: 0 none;
    cursor: pointer;

    &:hover,
    &:focus {
        svg {
            color: ${colors.ORANGE};
        }
    }
`;

const UploadImageIcon = styled(AddPhotoAlternateSharp)`
    position: relative;
    top: -2px;
`;

const ImageUpload = ({
    title,
    subTitle,
    hint,
    onUpload,
    onImageCaptureButtonClick,
    allowFileUpload,
    allowCameraUpload,
}) => {
    const { open, getInputProps } = useDropzone({
        multiple: false,
        accept: 'image/jpg, image/jpeg, image/png',
        onDrop: acceptedFiles => {
            if (acceptedFiles && acceptedFiles[0]) {
                const file = acceptedFiles[0];

                onUpload(file);
            }
        },
    });

    const isSingleButtonUpload = !allowFileUpload || !allowCameraUpload;

    const handleWrapperClick = () => {
        if (allowFileUpload && !allowCameraUpload) {
            open();
        } else if (allowCameraUpload && !allowFileUpload) {
            onImageCaptureButtonClick();
        }
    };

    const Wrapper = isSingleButtonUpload ? ClickableWrapper : SimpleWrapper;
    const Icon = isSingleButtonUpload ? SimpleIcon : IconButton;

    return (
        <Wrapper
            onClick={isSingleButtonUpload ? handleWrapperClick : undefined}
        >
            {allowFileUpload && <input {...getInputProps()} />}
            <Title>{title}</Title>
            {!!subTitle && <SubTitle>{subTitle}</SubTitle>}
            {!!hint && <Hint>{hint}</Hint>}
            <ButtonsWrapper>
                {allowFileUpload && (
                    <Icon
                        onClick={isSingleButtonUpload ? undefined : open}
                        title="Foto als Datei hochladen"
                    >
                        <UploadImageIcon />
                    </Icon>
                )}
                {allowCameraUpload && (
                    <Icon
                        onClick={
                            isSingleButtonUpload
                                ? undefined
                                : onImageCaptureButtonClick
                        }
                        title="Foto mit Kamera aufnehmen"
                    >
                        <PhotoCameraSharp />
                    </Icon>
                )}
            </ButtonsWrapper>
        </Wrapper>
    );
};

ImageUpload.defaultProps = {
    hint: null,
    onUpload: null,
    allowFileUpload: true,
    allowCameraUpload: true,
};

ImageUpload.propTypes = {
    title: PropTypes.any.isRequired,
    hint: PropTypes.any,
    onUpload: PropTypes.func,
    onImageCaptureButtonClick: PropTypes.func.isRequired,
    allowFileUpload: PropTypes.bool,
    allowCameraUpload: PropTypes.bool,
};

export default ImageUpload;
